import { Icons } from '@/app/components/ui/Icons'
import {
	DEFAULT_CURRENCY,
	FACILITIES,
	FILTER_TYPES,
	HOTEL_STYLE,
	INIT_DATA_STORAGE_KEY,
	STAY_OPTIONS,
} from '@/app/constants'
import { UserData } from '@/app/types'
import { useLocalStorage } from '@uidotdev/usehooks'
import { Currencies } from 'currencies-map'
import { useTranslations } from 'next-intl'
import { ReadonlyURLSearchParams, useRouter } from 'next/navigation'
import React from 'react'

interface AppliedFiltersProps {
	appliedFilters: {
		stars: number[]
		rating: number[]
		facilities: number[]
		stayOptions: number[]
		hotelStyles: number[]
		priceRange: [number, number] | undefined
	}
	searchParams: ReadonlyURLSearchParams
}

const AppliedFilters: React.FC<AppliedFiltersProps> = ({
	appliedFilters,
	searchParams,
}) => {
	const [initData, _] = useLocalStorage<UserData>(INIT_DATA_STORAGE_KEY)
	const userCurrency = initData?.selectedCurrency || initData?.defaultCurrency
	const t = useTranslations('Keywords')
	const filtersT = useTranslations('Filters')

	const router = useRouter()

	const removeFilter = (type: string, value: number | [number, number]) => {
		const newSearchParams = new URLSearchParams(searchParams)

		newSearchParams.delete('page')

		switch (type) {
			case FILTER_TYPES.STARS:
				newSearchParams.set(
					's',
					appliedFilters.stars.filter((star) => star !== value).join('|')
				)
				break
			case FILTER_TYPES.RATING:
				newSearchParams.delete('gr')
				break
			case FILTER_TYPES.HOTEL_STYLES:
				newSearchParams.set(
					't',
					appliedFilters.hotelStyles
						.filter((style) => style !== value)
						.join('|')
				)
				break
			case FILTER_TYPES.FACILITIES:
				newSearchParams.set(
					'fa',
					appliedFilters.facilities
						.filter((facility) => facility !== value)
						.join('|')
				)
				break
			case FILTER_TYPES.STAY_OPTIONS:
				newSearchParams.set(
					'p',
					appliedFilters.stayOptions
						.filter((option) => option !== value)
						.join('|')
				)
				break
			case FILTER_TYPES.PRICE_RANGE:
				newSearchParams.delete('mn')
				newSearchParams.delete('mx')
				break
		}

		if (newSearchParams.get('s') === '') newSearchParams.delete('s')
		if (newSearchParams.get('fa') === '') newSearchParams.delete('fa')
		if (newSearchParams.get('p') === '') newSearchParams.delete('p')
		if (newSearchParams.get('t') === '') newSearchParams.delete('t')
		if (newSearchParams.get('gr') === '') newSearchParams.delete('gr')

		const newUrl = `${window.location.pathname}?${newSearchParams.toString()}`
		router.push(newUrl)
	}

	const getFilterLabel = (type: string, value: number | [number, number]) => {
		const currency = Currencies.symbols.get(
			userCurrency?.abbreviation || (DEFAULT_CURRENCY as string)
		) as string
		const locale = 'en-US'

		switch (type) {
			case FILTER_TYPES.STARS:
				if (value === 0) return null
				if (value === 1) return t('oneStarHotels')
				return t('starHotels', { starCount: value as number })
			case FILTER_TYPES.PRICE_RANGE:
				if (Array.isArray(value) && value.length === 2) {
					const [min, max] = value
					return t('priceRange', {
						currency,
						min: Intl.NumberFormat(locale).format(min),
						max: Intl.NumberFormat(locale).format(max),
					})
				}
				return t('invalidPriceRange')
			case FILTER_TYPES.RATING:
				return t('ratingPlus', { value: value as number })
			case FILTER_TYPES.FACILITIES:
				const facilityKey = value as number
				// Find which group this facility ID belongs to
				const facilityGroup = FACILITIES.find((facility) => facility.id === facilityKey)
				const facilityName = facilityGroup?.name

				return facilityKey && facilityName
					? filtersT(`Amenities.${facilityName}`)
					: t('facilityValue', { value: String(facilityName) })
			case FILTER_TYPES.STAY_OPTIONS:
				const stayOptionValue = value as number
				const stayOptionName = STAY_OPTIONS.find(
					(stayOption) => stayOption.id === stayOptionValue
				)?.name

				return stayOptionName
					? filtersT(`StayOptions.${stayOptionName}`)
					: t('stayOptionValue', { value: stayOptionValue })
			case FILTER_TYPES.HOTEL_STYLES:
				const hotelStyleValue = value as number
				const hotelStyleName = HOTEL_STYLE.find(
					(hotelStyle) => hotelStyle.id === hotelStyleValue
				)?.name
				return hotelStyleName
					? filtersT(`HotelStyles.${hotelStyleName}`)
					: t('hotelStyleValue', { value: hotelStyleValue })
			default:
				return t('filterTypeValue', {
					type,
					value: Array.isArray(value) ? value.join('-') : value,
				})
		}
	}

	return (
		<div className="flex flex-wrap gap-2 mb-4">
			{Object.entries(appliedFilters).map(([type, values]) => {
				if (type === 'priceRangeLimits') return null
				if (type === 'priceRange' && Array.isArray(values)) {
					const [min, max] = values as [number, number]

					if (min === 0 && max === 0) {
						return null
					}

					return (
						<button
							key={`${type}-${min}-${max}`}
							onClick={() => removeFilter(type, [min, max])}
							className="font-semibold flex items-center text-text-900 transition-colors hover:bg-text-300 rounded-full px-3 py-2 text-sm border border-text-400 bg-white leading-[14px]"
						>
							{getFilterLabel(type, [min, max])}
							<Icons.iconClose className="ml-2 w-4 h-4" />
						</button>
					)
				}
				if (Array.isArray(values)) {
					return values.map((value) => {
						if (type === FILTER_TYPES.STARS && value === 0) return null
						return (
							<button
								key={`${type}-${value}`}
								onClick={() => removeFilter(type, value)}
								className="font-semibold flex items-center text-text-900 transition-colors hover:bg-text-300 rounded-full px-3 py-2 text-sm border border-text-400 bg-white leading-[14px]"
							>
								{getFilterLabel(type, value)}
								<Icons.iconClose className="ml-2 w-4 h-4" />
							</button>
						)
					})
				}
				return null
			})}
		</div>
	)
}

export default AppliedFilters
